import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Button, Loader, rem, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  DEFAULT_SHIFT_PLANNING,
  formatDuration,
  isCancelled,
  isExternal,
  minutesToHm,
  type AutofillPlan,
  type AutofillProposal,
  type MinuteRange,
} from '@reditelji/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAutofillApply, useEvent, useShiftMutations, useTimeline } from '../api/hooks';
import { useAllShifts, usePosts } from '../api/posts';
import { useSession } from '../api/session';
import type { PoolVolunteer, Shift, TimelinePayload, TimelinePost } from '../api/types';
import { rollupFor, type VolunteerRollup } from '../volunteers/derive';
import { useNow } from '../live/clock';
import { ciState, checkinsByShift, liveVals, onSiteCount } from '../live/derive';
import { dayPhase, plDod, plVrz } from '../lib/ui';
import { useUi } from '../state/ui';
import { Board, type BoardLens } from '../timeline/Board';
import {
  allDayShifts,
  assignmentPlan,
  availabilityBands,
  blockMatches,
  briefingStale,
  dayRefusalLabel,
  dayRefusals,
  declinedOnDay,
  rangeStillFree,
  DAY_SCOPE,
  dropPlan,
  fillTargets,
  gapMatches,
  gapMismatch,
  holderLabel,
  lensFits,
  nextTarget,
  planDay,
  planningOn,
  range,
  reasonChips,
  rosterPool,
  shiftClash,
  slotLabel,
  toAutofillInput,
  type DropPlan,
  type LensFit,
  type PlanScope,
} from '../timeline/derive';
import { ScopePopover, scopeLabel, withCeilings, type PlanCeilings } from '../timeline/ScopePopover';
import { AvailabilityConfirm, mustAsk, type PendingAssign } from '../timeline/AvailabilityConfirm';
import { gapKey, planHeadline, reviewSplit } from '../timeline/planReview';
import { typingInto, useNextTargetKey, usePlanKeys } from '../timeline/usePlanKeys';
import { AxisContext } from '../timeline/axisContext';
import { DayContext } from '../timeline/dayContext';
import { ScheduleContext } from '../timeline/scheduleContext';
import { makeAxis, xToMin } from '../timeline/geometry';
import { DragChip } from '../timeline/DragChip';
import { useDropPreview, type DropPreview } from '../timeline/dropPreview';
import { SidePanel } from '../timeline/SidePanel';
import { Toolbar } from '../timeline/Toolbar';
import '../timeline/timeline.css';

/**
 * The post row under a point, and only a row a person sees there: a scrolled
 * board slides its tracks under the sticky post labels and the hour axis, and
 * a point on those is not over a track at all.
 */
function rowAt(p: { x: number; y: number }): string | null {
  return (
    document.elementFromPoint(p.x, p.y)?.closest('[data-track-post]')?.getAttribute('data-track-post') ?? null
  );
}

export function TimelinePage() {
  const {
    selectedDayId,
    silentOverride,
    liveOverride,
    planOpen,
    setPlanOpen,
    selectedGap,
    selectGap,
    clearSelection,
    selectedShiftId,
    sectorFocus,
    setSectorFocus,
    collapsedGroups,
    toggleGroup,
    revealTimelineAt,
    filters,
    boardView,
    asideTab,
    setAsideTab,
    focusedProposal,
    focusProposal,
    planFocusOnly,
    setPlanFocusOnly,
    undo,
    setUndo,
    pendingResize,
    setPendingResize,
    lensVolunteerId,
    setLens,
  } = useUi();
  const timeline = useTimeline(selectedDayId);
  // Everything that changes the plan asks this (SPEC §1.1L); the organisation watches.
  const canPlan = useSession().can('plan:write');
  const shiftMut = useShiftMutations();
  const autofillApply = useAutofillApply();
  const eventInfo = useEvent();
  // The other days, for the hover card on a block. Small, and shared with Mesta
  // and Prostovoljci through the same query keys.
  const allShifts = useAllShifts();
  const posts = usePosts();
  const days = useMemo(() => eventInfo.data?.days ?? [], [eventInfo.data]);
  const [planned, setPlanned] = useState<AutofillPlan | null>(null);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  // What the next run covers and how long a day it may hand out. Session-only:
  // the stored rules in Nastavitve are what the next session starts from.
  const [scope, setScope] = useState<PlanScope>(DAY_SCOPE);
  const [ceilings, setCeilings] = useState<PlanCeilings | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<PendingAssign | null>(null);
  // The board as of the latest render. A confirmation dialog keeps an
  // assignment open at human speed, and the closures it will run were made at
  // drag time; they read the rows through here so a refetch in between cannot
  // hand the server a stale `updatedAt` or plan against a slot that has moved.
  const latest = useRef<TimelinePayload | null>(null);
  const clock = useNow();

  const storedPlanning = eventInfo.data?.shiftPlanning ?? null;
  // Memoised: the replan effect keys off this, and a fresh object every render
  // would have it replan forever.
  const planning = useMemo(
    () => (storedPlanning && ceilings ? withCeilings(storedPlanning, ceilings) : storedPlanning),
    [storedPlanning, ceilings],
  );

  useEffect(() => {
    setPlanned(null);
    setDismissed(new Set());
    setScope(DAY_SCOPE);
  }, [selectedDayId]);

  /**
   * Proposals carry coordinates from the snapshot they were planned against, so
   * a refetch replans from scratch rather than leaving a stale one acceptable.
   * Accepted pieces are covered by then and simply stop being proposed.
   */
  // The board's plan-mode state belongs to the plan. It ends when the
  // coordinator drops it and also on its own, once the last piece is accepted
  // and there is nothing left to decide.
  useEffect(() => {
    if (planned !== null) return;
    focusProposal(null);
    setPlanFocusOnly(false);
  }, [planned, focusProposal, setPlanFocusOnly]);

  const dataStamp = timeline.dataUpdatedAt;
  useEffect(() => {
    const fresh = timeline.data;
    if (!fresh || !planning) return;
    setPlanned((prev) => {
      if (prev === null) return null;
      // A plan with nothing left to decide closes itself: the day is filled.
      const next = planDay(fresh, planning, scope);
      return next.proposals.length === 0 && next.unfilled.length === 0 ? null : next;
    });
  }, [dataStamp, timeline.data, planning, scope]);

  // Rejecting hides a proposal without touching the roster, so it has to
  // survive the replan that follows the next accepted batch.
  const plan = useMemo(
    () =>
      planned === null
        ? null
        : { ...planned, proposals: planned.proposals.filter((p) => !dismissed.has(gapKey(p.gap))) },
    [planned, dismissed],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // Without a sensor nothing can be picked up: the board reads, it does not assign.
  const noSensors = useSensors();

  const payload = timeline.data ?? null;

  // live mode: auto on the event day, manual override via the toolbar switch
  const phase = payload ? dayPhase(payload.day.date, clock.isoDate) : 'next';
  const live = liveOverride ?? phase === 'live';
  const silentMode = silentOverride ?? live;
  const now = clock.minutes;
  // Mirrors the board's own gate (Board.tsx), so a drop cannot land on a block
  // the chips have taken off screen.
  const boardFiltering = filters.size > 0 && planningOn(live, phase) && boardView === 'mesta';

  // Rejecting the last proposal leaves a plan with nothing in it; that is not
  // a plan to review, so plan mode ends with it.
  const activePlan = plan && (plan.proposals.length > 0 || plan.unfilled.length > 0) ? plan : null;
  const proposals = activePlan?.proposals ?? [];
  // Cheap enough to derive on every render (pool × proposals), and it has to
  // agree between the strip, the panel and the keyboard walk, so it is derived
  // in one place.
  const review =
    activePlan && payload && planning
      ? reviewSplit(activePlan, payload.volunteers, planning, payload.day.key, silentMode)
      : null;
  const headline = activePlan && payload ? planHeadline(payload, proposals) : null;
  // The walk follows the review order: what needs a decision, then the rest.
  const walk = review ? [...review.exceptions.map((e) => e.proposal), ...review.clean] : [];

  // Podrobnosti is filling something — a gap, or an open row however it was
  // selected — and its candidate list has the keys.
  const selectedRow = selectedShiftId
    ? payload?.posts.flatMap((p) => p.shifts).find((s) => s.id === selectedShiftId)
    : undefined;
  // Only while Podrobnosti shows it: back on Predlog the plan has the keys again.
  const filling =
    asideTab === 'podrobnosti' &&
    (selectedGap !== null || (selectedRow !== undefined && selectedRow.state === 'open' && selectedRow.volunteerId === null));
  usePlanKeys({
    enabled: !filling,
    walk,
    focused: focusedProposal,
    onFocus: focusProposal,
    onAccept: acceptProposal,
    onReject: rejectProposal,
  });

  // Bring a post's row on screen at a minute: a sector focus that hides it is
  // dropped and a collapsed group opened, then the board scrolls to it.
  const revealPost = useCallback(
    (postId: string, fromMin: number) => {
      if (!payload) return;
      const p = payload.posts.find((x) => x.id === postId);
      if (!p) return;
      if (sectorFocus && payload.sectors.find((s) => s.id === p.sectorId)?.name !== sectorFocus) {
        setSectorFocus(null);
      }
      if (collapsedGroups[p.type]) toggleGroup(p.type);
      revealTimelineAt(payload.day.id, fromMin, postId);
    },
    [payload, sectorFocus, setSectorFocus, collapsedGroups, toggleGroup, revealTimelineAt],
  );

  // N: the next uncovered stretch of the day, in the order autofill fills them.
  // It lands on a row the board shows — a chip-filtered row is skipped, a
  // sector focus that hides it is dropped, a collapsed group is opened.
  const goNextTarget = useCallback(() => {
    if (!payload) return;
    const postOf = new Map(payload.posts.map((p) => [p.id, p]));
    // An open row selected by id stands where its gap would.
    const current =
      selectedGap ??
      (selectedRow && selectedRow.state === 'open' && selectedRow.volunteerId === null
        ? { postId: selectedRow.postId, fromMin: selectedRow.fromMin, toMin: selectedRow.toMin, shiftId: selectedRow.id }
        : null);
    // Only a stretch the board draws: under the chips an open row stands or
    // falls as its own block, a gap as its post's gaps.
    const t = nextTarget(payload, current, (x) => {
      const p = postOf.get(x.postId);
      if (!p) return false;
      if (!boardFiltering) return true;
      if (x.kind === 'gap') return gapMatches(filters, p);
      const row = p.shifts.find((s) => s.id === x.shiftId);
      return row !== undefined && blockMatches(filters, p, row, undefined);
    });
    if (!t) return;
    selectGap({
      postId: t.postId,
      fromMin: t.fromMin,
      toMin: t.toMin,
      ...(t.kind === 'open' ? { shiftId: t.shiftId } : {}),
    });
    revealPost(t.postId, t.fromMin);
  }, [payload, selectedGap, selectedRow, boardFiltering, filters, selectGap, revealPost]);
  useNextTargetKey({
    enabled:
      canPlan &&
      payload !== null &&
      planningOn(live, phase) &&
      boardView === 'mesta' &&
      !activePlan &&
      // A staffed shift's form is open: N is a letter it may be typing.
      (selectedShiftId === null || filling),
    onNext: goNextTarget,
  });

  // The person the board looks through (SPEC §1.1B), when that applies: on the
  // Prostovoljci tab of a day still to come, with no plan up — a plan's
  // proposals are not in the fit targets — and for somebody still in the pool.
  const boardPlanning = storedPlanning ?? DEFAULT_SHIFT_PLANNING;
  const lensPerson =
    payload && lensVolunteerId && asideTab === 'prostovoljci' && phase !== 'past' && !activePlan
      ? (rosterPool(payload).find((v) => v.id === lensVolunteerId) ?? null)
      : null;
  const lensList = useMemo(
    () => (payload && lensPerson ? lensFits(payload, boardPlanning, lensPerson.id) : []),
    [payload, lensPerson, boardPlanning],
  );
  const lensFitsByPost = useMemo(() => {
    const m = new Map<string, LensFit[]>();
    for (const f of lensList) m.set(f.post.id, [...(m.get(f.post.id) ?? []), f]);
    return m;
  }, [lensList]);
  // A drag in progress: its own Escape cancels it, which must not end the lens too.
  const dragging = useRef(false);
  // Where the pointer is during a drag. Read from the pointer events
  // themselves, in the capture phase, so it is current before dnd-kit's own
  // move handler runs — dnd-kit hands `over` to its move and end events a
  // render late, and a quick flick across a row edge would otherwise pair the
  // pointer with the row it just left.
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const stopTracking = useRef<(() => void) | null>(null);
  useEffect(() => () => stopTracking.current?.(), []);
  // The planner's input for a drag's previews, built once per board.
  const dropInput = useRef<{
    data: TimelinePayload;
    planning: typeof boardPlanning;
    input: ReturnType<typeof toAutofillInput>;
  } | null>(null);
  const lastDrop = useRef('');
  const setDropPreview = useDropPreview((st) => st.setPreview);
  // The drop target is the row `rowAt` finds, so the row's outline agrees with the preview.
  const pointerTrack = useCallback<CollisionDetection>(({ droppableContainers, pointerCoordinates }) => {
    const row = pointerCoordinates ? rowAt(pointerCoordinates) : null;
    const container = row ? droppableContainers.find((c) => c.id === `track:${row}`) : undefined;
    return container ? [{ id: container.id, data: { droppableContainer: container, value: 0 } }] : [];
  }, []);
  const lensOn = lensPerson !== null;
  const asking = pendingAssign !== null || pendingResize !== null;
  useEffect(() => {
    if (!lensOn) return;
    // Escape closes the nearest thing first. In the capture phase this runs
    // before a drag's own Escape (dnd-kit listens on the document) has
    // cancelled it, so the drag is still known to be on; and a menu, popover,
    // drawer or dialog that is open takes the key for itself.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (typingInto(e.target) || dragging.current || asking) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      setLens(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [lensOn, asking, setLens]);

  // The board's hours come from the day itself: Sunday's Dovje shifts run from
  // midnight, and a fixed 05:00 start would pin them to the left edge. Computed
  // before the early returns below, as every hook must be.
  const axis = useMemo(
    () => makeAxis(payload ? payload.posts.flatMap((p) => [...p.windows, ...p.shifts]) : []),
    [payload],
  );

  // Every board volunteer's whole event, for the hover card on a block. The
  // board payload is one day, so the other days come from /shifts and /posts —
  // both already cached by Mesta and Prostovoljci. Rolled up once per data
  // change so a hover is a map lookup rather than a scan per block.
  const scheduleMap = useMemo(() => {
    const map = new Map<string, VolunteerRollup>();
    if (!payload || !allShifts.data || !posts.data || days.length === 0) return map;
    const postById = new Map(posts.data.map((p) => [p.id, p]));
    for (const v of payload.volunteers) {
      map.set(v.id, rollupFor(v, allShifts.data, days, postById));
    }
    return map;
  }, [payload, allShifts.data, posts.data, days]);

  if (!selectedDayId || timeline.isPending) {
    return (
      <div className="tl-page" style={{ display: 'grid', placeItems: 'center' }}>
        <Loader />
      </div>
    );
  }
  if (!payload) return null;
  const data = payload;
  latest.current = data;

  /** Placing the person the board looks through is what the lens was for, so it ends. */
  function endLensFor(volunteerId: string) {
    if (useUi.getState().lensVolunteerId === volunteerId) setLens(null);
  }

  function createShift(input: {
    postId: string;
    fromMin: number;
    toMin: number;
    volunteerId: string;
    note: string;
    state?: string;
    forceSilent?: boolean;
  }) {
    endLensFor(input.volunteerId);
    shiftMut.create.mutate(
      {
        postId: input.postId,
        eventDayId: data.day.id,
        fromMin: input.fromMin,
        toMin: input.toMin,
        volunteerId: input.volunteerId,
        state: input.state ?? 'assigned',
        note: input.note,
        silent: input.forceSilent ?? silentMode,
      },
      {
        onSuccess: (created) => {
          if ((input.state ?? 'assigned') === 'assigned') {
            setUndo({
              head: 'Dodelitev dodana',
              sub: `${range(input.fromMin, input.toMin)}`,
              run: () => shiftMut.remove.mutate({ id: created.id, silent: true }),
            });
          }
        },
      },
    );
  }

  /**
   * Commit a resize the coordinator was asked about. `silent` is their answer
   * to the question the bar poses, so it overrides the toolbar's default.
   */
  function commitResize(silent: boolean) {
    const r = pendingResize;
    if (!r) return;
    setPendingResize(null);
    shiftMut.update.mutate(
      { id: r.shiftId, updatedAt: r.updatedAt, fromMin: r.fromMin, toMin: r.toMin, silent },
      {
        onSuccess: (updated) =>
          setUndo({
            head: `${r.postCode} — izmena spremenjena`,
            sub: `${r.name} · ${range(r.fromMin, r.toMin)}`,
            run: () =>
              shiftMut.update.mutate({
                id: r.shiftId,
                updatedAt: updated.updatedAt,
                fromMin: r.wasFromMin,
                toMin: r.wasToMin,
                silent: true,
              }),
          }),
      },
    );
  }

  /** The minute under the pointer on a post's row, measured now: the board may have scrolled. */
  function pointerMinute(postId: string, clientX: number): number | null {
    const track = document.querySelector<HTMLElement>(`[data-track-post="${CSS.escape(postId)}"]`);
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    return xToMin(axis, clientX - rect.left, rect.width);
  }

  /** What a drop of `volunteerId` at `minute` on the row would do — the preview and the drop alike. */
  function planDrop(volunteerId: string, postId: string, minute: number): DropPlan {
    if (dropInput.current?.data !== data || dropInput.current.planning !== boardPlanning) {
      dropInput.current = { data, planning: boardPlanning, input: toAutofillInput(data, boardPlanning) };
    }
    const volunteerOf = (id: string | null) => (id ? data.volunteers.find((v) => v.id === id) : undefined);
    return dropPlan(dropInput.current.input, data, volunteerId, postId, minute, {
      axisEnd: axis.toMin,
      // Only what the board shows: with a chip on, a stretch that looks empty
      // may still hold a filtered-out block, and taking it would be a material
      // change to somebody the coordinator never laid eyes on.
      shown: {
        shift: (post, sh) => !boardFiltering || blockMatches(filters, post, sh, volunteerOf(sh.volunteerId)),
        gap: (post) => !boardFiltering || gapMatches(filters, post),
      },
    });
  }

  function previewOf(plan: DropPlan, volunteer: PoolVolunteer, post: TimelinePost): DropPreview | null {
    if (!plan || plan.kind === 'same') return null;
    const base = { postId: post.id, postCode: post.code, name: volunteer.name };
    if (plan.kind === 'replace') {
      return {
        ...base,
        fromMin: plan.shift.fromMin,
        toMin: plan.shift.toMin,
        tone: 'replace',
        label: `namesto ${plan.holder ? holderLabel(plan.holder) : 'drugo osebo'}?`,
      };
    }
    const c = plan.candidate;
    const tone = !c || c.group === 'warn' ? 'warn' : c.group === 'rec' ? 'rec' : 'flag';
    const label = plan.clash
      ? `takrat že na ${plan.clash.postCode} ${range(plan.clash.fromMin, plan.clash.toMin)}`
      : c
        ? (reasonChips({ ...c, person: volunteer }, data, boardPlanning, true).find(
            (x) => x.tone === 'flag' || x.tone === 'warn',
          )?.text ?? 'ustreza')
        : 'ustreza';
    return { ...base, fromMin: plan.piece.fromMin, toMin: plan.piece.toMin, tone, label };
  }

  function handleDragStart(event: DragStartEvent) {
    dragging.current = true;
    lastDrop.current = '';
    const start = event.activatorEvent as PointerEvent;
    pointer.current = { x: start.clientX, y: start.clientY };
    const track = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
    };
    stopTracking.current?.();
    window.addEventListener('pointermove', track, true);
    stopTracking.current = () => {
      window.removeEventListener('pointermove', track, true);
      stopTracking.current = null;
    };
  }

  function handleDragMove(event: DragMoveEvent) {
    const volunteerId = event.active.data.current?.volunteerId as string | undefined;
    const at = pointer.current;
    const postId = at ? rowAt(at) : null;
    const minute = volunteerId && postId && at ? pointerMinute(postId, at.x) : null;
    if (!volunteerId || !postId || minute === null) {
      lastDrop.current = '';
      setDropPreview(null);
      return;
    }
    // A pixel is about a minute: plan again only when the minute under the pointer moves.
    // The board refetching under a drag (another coordinator's edit) plans again too.
    const key = `${dataStamp}:${volunteerId}:${postId}:${Math.floor(minute)}`;
    if (key === lastDrop.current) return;
    lastDrop.current = key;
    const volunteer = data.volunteers.find((v) => v.id === volunteerId);
    const post = data.posts.find((p) => p.id === postId);
    setDropPreview(volunteer && post ? previewOf(planDrop(volunteerId, postId, minute), volunteer, post) : null);
  }

  function handleDragCancel() {
    dragging.current = false;
    stopTracking.current?.();
    setDropPreview(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    dragging.current = false;
    stopTracking.current?.();
    setDropPreview(null);
    const volunteerId = event.active.data.current?.volunteerId as string | undefined;
    const at = pointer.current;
    const postId = at ? rowAt(at) : null;
    if (!volunteerId || !postId || !at) return;
    const volunteer = data.volunteers.find((v) => v.id === volunteerId);
    const post = data.posts.find((p) => p.id === postId);
    const minute = pointerMinute(postId, at.x);
    if (!volunteer || !post || minute === null) return;
    const plan = planDrop(volunteerId, postId, minute);
    if (!plan || plan.kind === 'same') return;

    if (plan.kind === 'replace') {
      dropReplace(post, volunteer, plan.shift);
      return;
    }
    if (plan.kind === 'fill') {
      // An open row comes whole, so `assignmentPlan` staffs it at its own
      // times; only an accepted trim narrows it.
      askThenAssign(post, volunteer, plan.piece.fromMin, plan.piece.toMin, (r, trimmed) =>
        assignToRange(post, volunteer, r, trimmed),
      );
      return;
    }
    askThenAssign(post, volunteer, plan.piece.fromMin, plan.piece.toMin, (r) =>
      createShift({
        postId: post.id,
        fromMin: r.fromMin,
        toMin: r.toMin,
        volunteerId: volunteer.id,
        note: 'dodano z vlečenjem iz nabora',
      }),
    );
  }

  /**
   * A drop onto somebody's izmena: reassigning a row that already has times,
   * so a trim edits those rather than leaving a gap beside them — the hours
   * handed back become uncovered on a post that read as covered, which is the
   * point.
   */
  function dropReplace(post: TimelinePost, volunteer: PoolVolunteer, hit: Shift) {
    const holder = hit.volunteerId
      ? data.volunteers.find((v) => v.id === hit.volunteerId)
      : undefined;
    askThenAssign(
      post,
      volunteer,
      hit.fromMin,
      hit.toMin,
      (r) => {
        // The dialog holds this open at human speed, and the board may have
        // refetched under it: commit against the row as it is now.
        const cur =
          (latest.current ?? data).posts
            .find((p) => p.id === post.id)
            ?.shifts.find((s) => s.id === hit.id) ?? hit;
        // Whoever the question named must still be the one replaced: if the
        // shift changed hands while it was open, nothing is taken silently.
        if (cur.volunteerId !== hit.volunteerId) {
          notifications.show({
            color: 'yellow',
            message: `${post.code} ${range(cur.fromMin, cur.toMin)} je medtem dobila drugo osebo — spusti znova.`,
          });
          return;
        }
        endLensFor(volunteer.id);
        const before = { volunteerId: cur.volunteerId, state: cur.state, fromMin: cur.fromMin, toMin: cur.toMin };
        // The rows already at the post, so an undo tells hours handed on
        // since from an overlap that was there all along.
        const known = new Set(
          ((latest.current ?? data).posts.find((p) => p.id === post.id)?.shifts ?? []).map((s) => s.id),
        );
        shiftMut.update.mutate(
          {
            id: hit.id,
            updatedAt: cur.updatedAt,
            volunteerId: volunteer.id,
            state: 'assigned',
            fromMin: r.fromMin,
            toMin: r.toMin,
            silent: silentMode,
          },
          {
            // Like every assignment's undo, this puts the row back as it was
            // (a proposal stays a proposal); a version bump the drop already
            // made stays, as it does for "Dodelitev dodana".
            onSuccess: (updated) =>
              setUndo({
                head: holder ? `${post.code} — ${volunteer.name} namesto ${holder.name}` : 'Dodelitev dodana',
                sub: `${volunteer.name} · ${range(r.fromMin, r.toMin)}`,
                run: () => {
                  const board = latest.current ?? data;
                  // Somebody marked not coming meanwhile cannot be put back
                  // (SPEC §1.1K); the server would refuse it as a bare error.
                  const was = before.volunteerId ? board.volunteers.find((v) => v.id === before.volunteerId) : undefined;
                  if (was && isCancelled(was)) {
                    notifications.show({
                      color: 'yellow',
                      message: `${was.name} medtem ne pride — ${post.code} ostane ${volunteer.name}.`,
                    });
                    return;
                  }
                  // A trim gave hours back; if somebody has taken them since,
                  // the row keeps its shortened times rather than stacking
                  // over theirs — only the holder comes back.
                  const now = board.posts.find((p) => p.id === post.id)?.shifts ?? [];
                  const widen = rangeStillFree(now, hit.id, before, known);
                  shiftMut.update.mutate({
                    id: hit.id,
                    updatedAt: updated.updatedAt,
                    volunteerId: before.volunteerId,
                    state: before.state,
                    ...(widen ? { fromMin: before.fromMin, toMin: before.toMin } : {}),
                    silent: true,
                  });
                  if (!widen) {
                    notifications.show({
                      color: 'yellow',
                      message: `${post.code} ostane ${range(r.fromMin, r.toMin)} — preostanek je medtem zaseden.`,
                    });
                  }
                },
              }),
          },
        );
      },
      holder,
      hit.volunteerId !== null,
    );
  }

  /**
   * Put `volunteer` on `requested` — a gap, or the piece of it an accepted trim
   * left. `trimmed` says which.
   *
   * A PROSTO slot the coordinator cut is staffed in place, at its own times: a
   * gap is only the uncovered part of a row, and a plain drop must not shrink
   * the row to it. Only an accepted trim narrows the slot — an assignment cut
   * to the hours somebody offered must not hand them the hours they said they
   * could not do, so the remainder goes back to being a gap for somebody else.
   */
  function assignToRange(
    post: TimelinePost,
    volunteer: PoolVolunteer,
    requested: MinuteRange,
    trimmed: boolean,
  ) {
    // Planned against the board as it is now: the confirmation dialog may have
    // held this open while another coordinator's edit refetched it.
    const current = (latest.current ?? data).posts.find((p) => p.id === post.id) ?? post;
    const plan = assignmentPlan(current, requested);
    if (plan.kind === 'staff') {
      endLensFor(volunteer.id);
      const openSlot = plan.shift;
      const fromMin = trimmed ? requested.fromMin : openSlot.fromMin;
      const toMin = trimmed ? requested.toMin : openSlot.toMin;
      shiftMut.update.mutate(
        {
          id: openSlot.id,
          updatedAt: openSlot.updatedAt,
          volunteerId: volunteer.id,
          state: 'assigned',
          fromMin,
          toMin,
          silent: silentMode,
        },
        {
          onSuccess: (updated) =>
            setUndo({
              head: 'Dodelitev dodana',
              sub: `${volunteer.name} · ${range(fromMin, toMin)}`,
              run: () => {
                // Compensating action: release only the hours just assigned.
                // The rest of a trimmed slot became a gap the moment it was
                // trimmed and may have been staffed since; re-opening it here
                // would stack an open row over somebody's shift.
                shiftMut.update.mutate({
                  id: openSlot.id,
                  updatedAt: updated.updatedAt,
                  volunteerId: null,
                  state: 'open',
                  fromMin,
                  toMin,
                  silent: true,
                });
              },
            }),
        },
      );
    } else {
      createShift({
        postId: post.id,
        fromMin: requested.fromMin,
        toMin: requested.toMin,
        volunteerId: volunteer.id,
        note: 'dodeljeno iz seznama razpoložljivih',
      });
    }
    // An open row is selected by id as well as by range; both go, so the panel
    // closes rather than turning to the row just staffed.
    clearSelection();
  }

  /**
   * Every drag onto the board goes through here — from the pool onto a gap, a
   * track, or a staffed izmena. The side panel asks the same questions in place,
   * on the candidate's row or in the save bar, and assigns directly.
   *
   * The check is a question, never a refusal: a coordinator may have settled it
   * by phone, and SPEC §3's absoluteness governs the autofill machine, not the
   * human. But it must not be possible to do it by accident, so the same dialog
   * guards every assignment gesture. Moving an existing shift's edges is a
   * different act with its own confirmation (the resize dialog), which asks the
   * same availability question; live-mode handover moves ask nothing, since on
   * the day what somebody wrote on a signup sheet changes nothing.
   *
   * `commit` learns whether the range it gets is a trim of the one asked for.
   * `displaced` is the person this assignment takes the shift from, so their
   * retired briefing is weighed too.
   */
  function askThenAssign(
    post: TimelinePost,
    volunteer: PoolVolunteer,
    fromMin: number,
    toMin: number,
    commit: (range: MinuteRange, trimmed: boolean) => void,
    displaced?: PoolVolunteer,
    /** The shift has a holder this takes it from, even one the payload no longer names. */
    takesFromSomebody = false,
  ) {
    const range = { fromMin, toMin };
    const closed = gapMismatch(volunteer, range, data.day.key);
    // A shift they turned down on this day, not their status: that reads
    // "zavrnjen" for a refusal on any day (SPEC §1.1F).
    const declined = declinedOnDay(volunteer, data);
    const replaces = takesFromSomebody ? (displaced?.name ?? 'drugo osebo') : undefined;
    // Standing at two posts, or past the daily maximum: what the drag preview
    // drew in red is asked here, never silently done (SPEC §3: a question, not
    // a refusal).
    const passesMax = (r: MinuteRange) =>
      !isExternal(volunteer) && volunteer.dayMinutes + (r.toMin - r.fromMin) > boardPlanning.maxDailyMin;
    const clashRow = shiftClash(data, volunteer.id, range);
    const clash = clashRow
      ? `${clashRow.postCode} ${minutesToHm(clashRow.fromMin)}–${minutesToHm(clashRow.toMin)}`
      : undefined;
    const afterMin = volunteer.dayMinutes + (toMin - fromMin);
    const overMax = passesMax(range) ? { afterMin, maxMin: boardPlanning.maxDailyMin } : undefined;
    if (!mustAsk({ closed, declined, replaces, clash, overMax })) {
      commit(range, false);
      return;
    }
    // A silent edit resets nobody's confirmation, so under it a sent briefing
    // is not something to weigh at all — neither the newcomer's nor the
    // displaced holder's.
    const displacedBriefed =
      displaced !== undefined &&
      !isExternal(displaced) &&
      // Somebody not coming holds no briefing a move could retire (SPEC §1.1K).
      !isCancelled(displaced) &&
      !briefingStale(displaced);
    setPendingAssign({
      volunteer,
      postId: post.id,
      postCode: post.code,
      postMicro: post.micro,
      range,
      dayKey: data.day.key,
      closed,
      declined,
      clash,
      overMax,
      stillClashes: (r) => shiftClash(data, volunteer.id, r) !== null || passesMax(r),
      refusals: dayRefusals(volunteer, data).map((r) => dayRefusalLabel(data, r)),
      replaces,
      retiresBriefing: !silentMode && !briefingStale(volunteer),
      displaced: !silentMode && displacedBriefed ? displaced.name : undefined,
      commit: (r) => commit(r, r.fromMin !== range.fromMin || r.toMin !== range.toMin),
    });
  }

  /**
   * The side panel's Dodeli: `piece` has already been weighed there. `narrows`
   * when the piece is shorter than the gap or open row it was cut from, which
   * is what shrinks an open row to it.
   */
  function assignPiece(volunteer: PoolVolunteer, piece: MinuteRange & { postId: string }, narrows: boolean) {
    const post = data.posts.find((p) => p.id === piece.postId);
    if (!post) return;
    assignToRange(post, volunteer, { fromMin: piece.fromMin, toMin: piece.toMin }, narrows);
  }

  /** A fit on the board or in "Kam lahko": bring its row on screen, then place the person. */
  function assignFit(fit: LensFit) {
    if (!lensPerson) return;
    revealPost(fit.post.id, fit.piece.fromMin);
    assignPiece(lensPerson, { ...fit.piece, postId: fit.post.id }, fit.narrows);
  }

  const canActLens = canPlan && phase !== 'past';
  const boardLens: BoardLens | null = lensPerson
    ? {
        volunteerId: lensPerson.id,
        name: lensPerson.name,
        bands: availabilityBands(lensPerson.availability, data.day.key),
        fitsByPost: lensFitsByPost,
        canAct: canActLens,
        onAssign: assignFit,
      }
    : null;

  function runAutofill() {
    if (!planning) return;
    setScopeOpen(false);
    setDismissed(new Set());
    setPlanned(planDay(data, planning, scope));
    setAsideTab('predlog');
    focusProposal(null);
    // The plan speaks about a fifth of the board; the rest is noise until it
    // is decided. The strip toggles the other rows back on.
    setPlanFocusOnly(true);
  }

  function closePlan() {
    setPlanned(null);
  }

  /** Send accepted proposals as one request: the server applies them or none. */
  function apply(picked: AutofillProposal[], onApplied?: () => void) {
    if (picked.length === 0) return;
    // Slots the coordinator drew themselves are staffed in place, so undoing
    // has to hand them back empty — deleting them would destroy their work.
    const staffed = new Set(
      picked.map((p) => p.gap.targetShiftId).filter((id): id is string => id !== null),
    );
    autofillApply.mutate(
      {
        eventDayId: data.day.id,
        silent: silentMode,
        assignments: picked.map((p) => ({
          postId: p.gap.postId,
          volunteerId: p.volunteerId,
          fromMin: p.gap.fromMin,
          toMin: p.gap.toMin,
          shiftId: p.gap.targetShiftId,
        })),
      },
      {
        onSuccess: (res) => {
          onApplied?.();
          setUndo({
            head: picked.length === 1 ? 'Dodelitev dodana' : `Dodeljenih ${picked.length} izmen`,
            sub: res.byVolunteer.map((v) => v.name).join(', '),
            run: () => {
              for (const row of res.shifts) {
                if (staffed.has(row.id)) {
                  shiftMut.update.mutate({
                    id: row.id,
                    updatedAt: row.updatedAt,
                    volunteerId: null,
                    state: 'open',
                    silent: true,
                  });
                } else {
                  shiftMut.remove.mutate({ id: row.id, silent: true });
                }
              }
            },
          });
        },
      },
    );
  }

  function acceptProposal(p: AutofillProposal) {
    apply([p]);
  }

  function acceptMany(picked: AutofillProposal[]) {
    apply(picked);
  }

  function rejectProposal(p: AutofillProposal) {
    setDismissed((prev) => new Set(prev).add(gapKey(p.gap)));
  }

  /**
   * Take the batch that bends no rule and leave the plan up: the refetch
   * replans against the new roster, so what stays on screen is exactly what
   * still needs a decision.
   */
  function acceptClean() {
    if (review) apply(review.clean);
  }

  // Each uncovered stretch once — a PROSTO row, or a gap with the rows cut out,
  // including the open rows of a post with no window at all.
  const gapCount = fillTargets(data).length;
  const freeCount = rosterPool(data).filter((v) => v.dayMinutes === 0).length;
  const underTwo = rosterPool(data).filter((v) => v.dayMinutes > 0 && v.dayMinutes < 120).length;

  const cm = checkinsByShift(data.checkins);
  const dayShiftsAll = allDayShifts(data);
  const vals = live ? liveVals(data, now, true) : null;
  const onSite = vals ? vals.onSite : onSiteCount(dayShiftsAll, cm);
  const lateCount = vals?.lateList.length ?? 0;
  const doneCount = dayShiftsAll.filter(
    (s) => s.state === 'done' || ciState(s, cm) === 'opravljeno',
  ).length;

  return (
    <DndContext
      sensors={canPlan ? sensors : noSensors}
      collisionDetection={pointerTrack}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <DragChip pointer={pointer} />
      <div className="tl-page">
        <Toolbar timeline={data} phase={phase} live={live} silent={silentMode} now={now} lensOn={lensOn} />
        {pendingResize ? (
          <div className="tl-undo-strip tl-undo-strip--ask">
            <span className="tl-undo-head">
              {pendingResize.postCode} · {range(pendingResize.fromMin, pendingResize.toMin)}
            </span>
            <span className="tl-undo-sub">
              {pendingResize.name} —{' '}
              {[
                pendingResize.reason === 'potrjen'
                  ? 'izmena je že potrjena, sprememba jo vrne v čakanje'
                  : pendingResize.reason === 'brifing'
                    ? 'brifing je že poslan, sprememba ga razveljavi'
                    : null,
                pendingResize.closed.length > 0
                  ? `v prijavnici »ni na voljo« za ${pendingResize.closed.map(slotLabel).join(', ')}`
                  : null,
              ]
                .filter(Boolean)
                .join('; ')}
              .
            </span>
            <Button size="compact-xs" onClick={() => commitResize(false)}>
              Shrani
            </Button>
            <Button size="compact-xs" variant="default" onClick={() => commitResize(true)}>
              Shrani brez ponovne potrditve
            </Button>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              title="prekliči"
              onClick={() => setPendingResize(null)}
            >
              ✕
            </Button>
          </div>
        ) : null}
        {undo ? (
          <div className="tl-undo-strip">
            <span className="tl-undo-head">{undo.head}</span>
            <span className="tl-undo-sub">{undo.sub}</span>
            {undo.alt ? (
              <Button size="compact-xs" onClick={() => undo.alt!.run()}>
                {undo.alt.label}
              </Button>
            ) : null}
            <Button
              size="compact-xs"
              variant="default"
              onClick={() => {
                undo.run();
                setUndo(null);
              }}
            >
              Razveljavi
            </Button>
            <Button size="compact-xs" variant="subtle" color="gray" title="zapri" onClick={() => setUndo(null)}>
              ✕
            </Button>
          </div>
        ) : null}
        {lensPerson && boardView === 'mesta' ? (
          <div className="tl-undo-strip tl-lens-strip">
            <span className="tl-undo-head">{lensPerson.name}</span>
            <span className="tl-lens-legend">
              <span><i className="tl-lens-swatch tl-fit" />ustreza</span>
              <span><i className="tl-lens-swatch tl-fit tl-fit--warn" />z opozorilom</span>
              <span><i className="tl-lens-swatch tl-fit tl-fit--only" />edini priporočeni</span>
              <span><i className="tl-lens-swatch tl-lens-closed" />ni na voljo</span>
              <span><i className="tl-lens-swatch tl-lens-unknown" />ni podatka</span>
            </span>
            <span style={{ flex: 1 }} />
            <Button size="compact-xs" variant="default" onClick={() => setLens(null)}>
              Končaj ✕
            </Button>
          </div>
        ) : null}
        <AxisContext.Provider value={axis}>
          <DayContext.Provider value={data.day.key}>
          <ScheduleContext.Provider value={scheduleMap}>
            <div className="tl-main">
              <Board
                data={data}
                proposals={proposals}
                unfilled={activePlan?.unfilled ?? []}
                phase={phase}
                live={live}
                now={now}
                onAcceptProposal={acceptProposal}
                onRejectProposal={rejectProposal}
                lens={boardLens}
              />
              <SidePanel
                data={data}
                plan={activePlan}
                review={review}
                headline={headline}
                phase={phase}
                live={live}
                now={now}
                planning={storedPlanning ?? DEFAULT_SHIFT_PLANNING}
                silent={silentMode}
                onAssignPiece={assignPiece}
                onAcceptClean={acceptClean}
                onAcceptMany={acceptMany}
                onRejectAll={closePlan}
                onAcceptProposal={acceptProposal}
                onRejectProposal={rejectProposal}
                lens={lensPerson ? { person: lensPerson, fits: lensList, canAct: canActLens, onAssign: assignFit } : null}
              />
            </div>
          </ScheduleContext.Provider>
          </DayContext.Provider>
        </AxisContext.Provider>
        {phase === 'past' ? (
          <div className="tl-bottombar">
            <span className="tl-plandone-pill">ZAKLJUČENO</span>
            <span style={{ fontSize: rem(12), color: 'oklch(0.55 0.012 90)' }}>
              {data.day.label} je zaključen — razpored je arhiviran, urejanje ni več
              potrebno.
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: rem(12.5), color: 'oklch(0.42 0.06 155)' }}>
              {doneCount} opravljenih izmen
            </span>
          </div>
        ) : activePlan && review && headline ? (
          <div className="tl-bottombar tl-bottombar--plan">
            <span className="tl-plan-pill">PREDLOG</span>
            <span className="tl-plan-metric">{plDod(proposals.length)}</span>
            {scope.kind !== 'day' ? (
              <span className="tl-plan-scope">{scopeLabel(scope, sectorFocus)}</span>
            ) : null}
            <span className="tl-plan-cov">
              <span>{headline.coverageNow} %</span>
              <span className="tl-plan-arrow">→</span>
              <span className="tl-plan-cov-after">{headline.coverageAfter} %</span>
            </span>
            {review.exceptions.some((e) => e.kinds[0] !== 'reconfirm') ? (
              <span className="tl-plan-warn">
                {review.exceptions.filter((e) => e.kinds[0] !== 'reconfirm').length} za pregled
              </span>
            ) : (
              <span>brez opozoril</span>
            )}
            {review.exceptions.some((e) => e.kinds[0] === 'reconfirm') ? (
              <span>
                {review.exceptions.filter((e) => e.kinds[0] === 'reconfirm').length} znova potrjuje
              </span>
            ) : null}
            {activePlan.unfilled.length > 0 ? (
              <span className="tl-plan-warn">{plVrz(activePlan.unfilled.length)} ostaja</span>
            ) : null}
            <button
              type="button"
              className={`tl-plan-toggle${planFocusOnly ? ' tl-plan-toggle--on' : ''}`}
              title="pokaži samo vrstice, o katerih predlog kaj pove"
              onClick={() => setPlanFocusOnly(!planFocusOnly)}
            >
              <span className="tl-plan-box">{planFocusOnly ? '✓' : ''}</span>
              samo predlagane vrstice
            </button>
            <span className="tl-plan-keys">↵ sprejmi · ⌫ zavrni · J/K premik</span>
            <span style={{ flex: 1 }} />
            <Button
              size="compact-sm"
              style={{ background: 'oklch(0.35 0.05 245)' }}
              disabled={review.clean.length === 0}
              loading={autofillApply.isPending}
              onClick={acceptClean}
            >
              {review.clean.length === 1
                ? 'Sprejmi 1 čistega'
                : `Sprejmi ${review.clean.length} čistih`}
            </Button>
            <Button size="compact-sm" variant="default" onClick={() => setAsideTab('predlog')}>
              Pregled ▸
            </Button>
            <Button size="compact-sm" variant="default" onClick={closePlan}>
              Zavrni predlog
            </Button>
          </div>
        ) : live && (!planOpen || !canPlan) ? (
          <div className="tl-bottombar">
            {canPlan ? (
              <>
                <Button size="compact-sm" variant="default" onClick={() => setPlanOpen(true)}>
                  Načrtovanje ▴
                </Button>
                <span style={{ fontSize: rem(12), color: 'oklch(0.55 0.012 90)' }}>
                  samodejno zapolni, okna mest in bazen prostih so med dogodkom umaknjeni
                </span>
              </>
            ) : null}
            <span style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: 'var(--rr-font-mono)',
                fontSize: rem(13),
                color: 'oklch(0.35 0.08 230)',
                whiteSpace: 'nowrap',
              }}
            >
              {onSite} na terenu
            </span>
            <span style={{ fontSize: rem(12), color: 'oklch(0.55 0.12 25)', whiteSpace: 'nowrap' }}>
              {lateCount} brez prijave
            </span>
          </div>
        ) : (
          <div className="tl-bottombar">
            <Text size="sm" fw={600} component="span">
              {freeCount}
            </Text>
            <span>razpoložljivih</span>
            {underTwo > 0 ? (
              <span title="prostovoljci z manj kot 2 h razporejenimi">
                {underTwo} z manj kot 2 h
              </span>
            ) : null}
            <span style={{ color: gapCount ? 'oklch(0.55 0.12 25)' : undefined }}>
              {gapCount ? `${gapCount} vrzeli` : 'brez vrzeli'}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ color: 'var(--rr-text-faint)', fontSize: rem(12) }}>
              nepokrito: {formatDuration(data.stats.gapMin)}
            </span>
            {canPlan ? (
              <>
                <Button
                  component={Link}
                  to="/nastavitve"
                  size="compact-sm"
                  variant="default"
                  title="Nastavitve predlogov — dolžina izmene in dan prostovoljca"
                >
                  ⚙
                </Button>
                <Button
                  size="compact-sm"
                  style={{ background: 'oklch(0.35 0.05 245)' }}
                  disabled={gapCount === 0 || !planning}
                  loading={autofillApply.isPending}
                  onClick={runAutofill}
                >
                  Samodejno zapolni
                </Button>
                {storedPlanning ? (
                  <ScopePopover
                    data={data}
                    planning={storedPlanning}
                    scope={scope}
                    ceilings={ceilings ?? {
                      softDailyMin: storedPlanning.softDailyMin,
                      maxDailyMin: storedPlanning.maxDailyMin,
                    }}
                    sectorFocus={sectorFocus}
                    opened={scopeOpen}
                    onOpenChange={setScopeOpen}
                    onScope={setScope}
                    onCeilings={setCeilings}
                    onRun={runAutofill}
                  />
                ) : null}
                {live ? (
                  <Button
                    size="compact-sm"
                    variant="default"
                    title="skrij načrtovalna orodja"
                    onClick={() => setPlanOpen(false)}
                  >
                    ▾
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        )}
      </div>
      <AvailabilityConfirm
        pending={pendingAssign}
        onCancel={() => setPendingAssign(null)}
        onAssign={(r) => {
          const p = pendingAssign;
          setPendingAssign(null);
          p?.commit(r);
        }}
      />
    </DndContext>
  );
}
